import React, { useState, useEffect } from 'react';
import { useSubdomain } from '../contexts/SiteContext';
import moduleService from '../services/moduleService';
import './PlanManagement.css';

const PlanManagement = () => {
  const { sites, loadSites } = useSubdomain();
  const [sitePlans, setSitePlans] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const planDefinitions = moduleService.getPlanDefinitions();

  // Cargar planes de todos los sites
  useEffect(() => {
    loadSitePlans();
  }, [sites]);

  const loadSitePlans = async () => {
    if (sites.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      const plansData = {};
      for (const site of sites) {
        try {
          const response = await moduleService.getSitePlan(site.id);
          plansData[site.id] = response.plan || 'BASIC';
        } catch (error) {
          console.warn(`Error loading plan for site ${site.id}:`, error);
          plansData[site.id] = 'BASIC'; // Default plan
        }
      }
      setSitePlans(plansData);
    } catch (error) {
      setError('Error cargando planes de los sites');
      console.error('Error loading site plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = async (siteId, newPlanId) => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      await moduleService.updateSitePlan(siteId, newPlanId);
      
      // Actualizar estado local
      setSitePlans(prev => ({
        ...prev,
        [siteId]: newPlanId
      }));

      // Recargar sites para obtener información actualizada
      await loadSites(false, true);

      setSuccessMessage(`Plan actualizado exitosamente para ${sites.find(s => s.id === siteId)?.name}`);
    } catch (error) {
      setError(`Error actualizando plan: ${error.message}`);
      console.error('Error updating site plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPlanFeatures = (planId) => {
    const plan = planDefinitions[planId];
    return plan ? plan.features : [];
  };

  const formatPrice = (price, currency, period) => {
    if (price === 0) return 'Gratis';
    return `$${price}/${period === 'month' ? 'mes' : period}`;
  };

  const clearMessages = () => {
    setError(null);
    setSuccessMessage(null);
  };

  return (
    <div className="plan-management">
      <div className="plan-management-header">
        <h2>Gestión de Planes</h2>
        <p>Administra los planes de suscripción de cada centro deportivo</p>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          <span className="alert-text">{error}</span>
          <button onClick={clearMessages} className="alert-close">×</button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span>
          <span className="alert-text">{successMessage}</span>
          <button onClick={clearMessages} className="alert-close">×</button>
        </div>
      )}

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <span>Cargando...</span>
        </div>
      )}

      <div className="plan-definitions">
        <h3>Planes Disponibles</h3>
        <div className="plans-grid">
          {Object.entries(planDefinitions).map(([planId, plan]) => (
            <div key={planId} className="plan-card">
              <div className="plan-header">
                <h4 className="plan-name">{plan.name}</h4>
                <div className="plan-price">
                  {formatPrice(plan.price, plan.currency, plan.period)}
                </div>
              </div>
              <div className="plan-features">
                <h5>Módulos incluidos:</h5>
                <ul>
                  {plan.features.map(moduleId => (
                    <li key={moduleId}>
                      {moduleService.getModuleDefinitions()[moduleId]?.name || moduleId}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="sites-plans">
        <h3>Planes por Centro Deportivo</h3>
        {sites.length === 0 ? (
          <div className="no-sites">
            <p>No hay centros deportivos registrados.</p>
          </div>
        ) : (
          <div className="sites-table">
            <div className="table-header">
              <div className="col-site">Centro Deportivo</div>
              <div className="col-plan">Plan Actual</div>
              <div className="col-modules">Módulos Habilitados</div>
              <div className="col-actions">Acciones</div>
            </div>
            
            {sites.map(site => (
              <div key={site.id} className="table-row">
                <div className="col-site">
                  <div className="site-info">
                    <h4>{site.name}</h4>
                    <p>{site.description}</p>
                  </div>
                </div>
                
                <div className="col-plan">
                  <select
                    value={sitePlans[site.id] || 'BASIC'}
                    onChange={(e) => handlePlanChange(site.id, e.target.value)}
                    disabled={loading}
                    className="plan-selector"
                  >
                    {Object.entries(planDefinitions).map(([planId, plan]) => (
                      <option key={planId} value={planId}>
                        {plan.name} - {formatPrice(plan.price, plan.currency, plan.period)}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="col-modules">
                  <div className="modules-list">
                    {getPlanFeatures(sitePlans[site.id] || 'BASIC').map(moduleId => (
                      <span key={moduleId} className="module-tag">
                        {moduleService.getModuleDefinitions()[moduleId]?.name || moduleId}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="col-actions">
                  <button
                    onClick={() => handlePlanChange(site.id, sitePlans[site.id] || 'BASIC')}
                    disabled={loading}
                    className="btn btn-primary btn-sm"
                  >
                    Actualizar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanManagement;
